
import { supabase } from "@/integrations/supabase/client";
import { Reagent } from "@/types/reagent.types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger("CreateBulkService");

// Create multiple reagents from CSV data
export const createReagentsFromCSV = async (reagents: Array<Partial<Reagent>>): Promise<{
  success: boolean;
  successCount: number;
  errorCount: number;
  errors: string[];
}> => {
  logger.info(`Creating ${reagents.length} reagents from CSV`);
  
  const result = {
    success: false,
    successCount: 0,
    errorCount: 0,
    errors: [] as string[]
  };

  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user?.id) {
      logger.error("No authenticated user found");
      throw new Error("You must be logged in to upload reagents");
    }

    // Process each reagent row
    for (const reagent of reagents) {
      try {
        // Ensure the required fields are present
        if (!reagent.name) {
          result.errorCount++;
          result.errors.push(`Missing required field 'name' for a reagent entry`);
          continue;
        }
        
        // Prepare data for insertion, ensuring number_of_packages is a number
        const reagentData = {
          name: reagent.name,
          description: reagent.description || "",
          catalogue_number: reagent.catalogNumber || reagent.catalogue_number || "",
          category_id: reagent.category || reagent.category_id || "",
          amount: parseFloat(reagent.amount?.toString() || "0"),
          amount_unit: reagent.amount_unit || "",
          storage_conditions: reagent.storageConditions || reagent.storage_conditions || "",
          number_of_packages: typeof reagent.numberOfPackages === 'string' || typeof reagent.number_of_packages === 'string' 
            ? parseInt(reagent.numberOfPackages?.toString() || reagent.number_of_packages?.toString() || "1", 10) 
            : (reagent.numberOfPackages || reagent.number_of_packages || 1),
          user_id: session.user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          available: true
        };
        
        const { data: insertedData, error } = await supabase.from('reagents').insert(reagentData).select();
        
        if (error) {
          logger.error(`Error creating reagent ${reagent.name}:`, error);
          result.errorCount++;
          result.errors.push(`${reagent.name}: ${error.message}`);
        } else if (insertedData && insertedData.length > 0) {
          result.successCount++;
          logger.info(`Reagent ${reagent.name} created successfully, notifications handled by database trigger`);
        }
      } catch (error) {
        logger.error(`Exception processing reagent ${reagent.name}:`, error);
        result.errorCount++;
        result.errors.push(`${reagent.name || 'Unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    result.success = result.successCount > 0;
    logger.info(`CSV upload completed: ${result.successCount} succeeded, ${result.errorCount} failed`);
    
    return result;
  } catch (error) {
    logger.error("Exception in createReagentsFromCSV:", error);
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    return result;
  }
};
